import React, { Reducer, useEffect, useReducer, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HexColorPicker } from 'react-colorful';
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from '../../../Common/UI/Dialog';
import Separator from '../../../Common/UI/Separator';
import { Button } from '../../../Common/UI/Button';
import { notifySuccess } from '../../../Common/UI/Notify';
import Icons from '../../../Common/Icons/Icons';
import { ApplicationContainer } from '../index';
import { MenuItem, useMenuProvider } from '../../../../context/menuHeader';
import { generateMenuItems, routes as infoTracRoutes } from '../../../../infoTrac/components/Layout/Menu';
import { Switch } from '../../../Common/UI/Switch';
import {
  getAllSettingsFromStorage,
  getGuidanceSettingsFromStorage,
  setGuidanceSettingsInStorage,
  setInfoTracSettingsInStorage,
} from './storage-utils';
import Label from '../../../Common/UI/Label';
import { cn } from '../../../../utils/styles';
import PreviewDashboard from '../../../../Guidance/pages/dashboard/preview';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../Common/UI/Select';
import { Input } from '../../../Common/UI/Input';
import { RadioGroup, RadioGroupItem } from '../../../Common/UI/Radio';
import EmailSender from './EmailSender';
import { initialJobStatusData, JOBS_STORAGE_KEY } from '../../../../infoTrac/components/Pages/JobStatus/data/jobStatus';

interface IState {
  [key: string]: boolean | string; // { hidden: boolean }
}

export type EnrollmentStepType = 'by-account' | 'global';

export interface GuidanceSettingProps {
  showCaptcha: boolean;
  showPdfViewer: boolean;
  enrollmentStep3: EnrollmentStepType;
  primaryColor: string;
  secondaryColor: string;
  logo: string;
  deliveryTypeMonthly?: 'electronic' | 'paper' | 'both';
  deliveryTypeVisa?: 'electronic' | 'paper' | 'both';
}

const defaultGuidanceValues: GuidanceSettingProps = {
  showCaptcha: false,
  showPdfViewer: true,
  enrollmentStep3: 'by-account',
  primaryColor: '#54C1FB',
  secondaryColor: '#272848',
  logo: '', // /guidance/logo.png
};

const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const colorPickers: { colorType: ColorType; label: string }[] = [
  { colorType: 'primary', label: 'Primary' },
  { colorType: 'secondary', label: 'Secondary' },
];

type ColorType = 'primary' | 'secondary';

const labelStyles = 'text-base text-primary-text';

function SettingsDialog() {
  // const [logoImage, setLogoImage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const { handleTriggerRegenerate } = useMenuProvider();
  const [settingItems, setSettingItems] = useState<MenuItem[]>([]);
  const [activeColorPicker, setActiveColorPicker] = useState<ColorType>('primary');

  /** If settings was never saved in storage on first render - save it. */
  useEffect(() => {
    const { showCaptcha } = getGuidanceSettingsFromStorage();

    if (showCaptcha === null || showCaptcha === undefined) {
      handleSaveGuidance();
    }
  }, []);

  /** Get initial value for InfoTrac form from storage */
  const [formDefaultValue, setFormDefaultValue] = useState<IState>(getAllSettingsFromStorage());

  const { register, handleSubmit, control, getValues, reset } = useForm({
    defaultValues: formDefaultValue,
  });

  useEffect(() => {
    const items = generateMenuItems(infoTracRoutes);

    setSettingItems(items);
  }, [infoTracRoutes]);

  /** InfoTrac save: */
  const handleSaveInfoTrac = (infoTracData: IState) => {
    setInfoTracSettingsInStorage(infoTracData);
  };
  const handleSaveGuidance = () => {
    setGuidanceSettingsInStorage(guidanceState);
  };

  /** Guidance / eStatements */
  const reducer = (prevState: GuidanceSettingProps, action: Partial<GuidanceSettingProps>) => ({
    ...prevState,
    ...action,
  });
  const [guidanceState, dispatch] = useReducer<Reducer<GuidanceSettingProps, Partial<GuidanceSettingProps>>>(
    reducer,
    defaultGuidanceValues
  );

  const handleReset = () => {
    dispatch(defaultGuidanceValues);
  };

  /** Reset infoTrac settings on modal close */
  useEffect(() => {
    if (!modalOpen) {
      const newVal = getAllSettingsFromStorage();

      setFormDefaultValue(newVal);
      setTimeout(() => reset(newVal), 300);
    }
  }, [modalOpen]);
  /** Reset Guidance */
  useEffect(() => {
    /** Set initial values for guidance part of the form */
    const guidanceStorageValue: GuidanceSettingProps = getGuidanceSettingsFromStorage();

    if (guidanceStorageValue && JSON.stringify(guidanceStorageValue) !== '{}') {
      dispatch(guidanceStorageValue);
    }
  }, [modalOpen]);

  const handleResetSession = () => {
    localStorage.clear();
    notifySuccess('Session reset successfully', 1000);
    setTimeout(() => window.location.reload(), 2000);
  };

  const handleResetDivertFeature = () => {
    localStorage.setItem(JOBS_STORAGE_KEY, JSON.stringify(initialJobStatusData));
    notifySuccess('Divert Feature reset successfully', 1000);
    setTimeout(() => window.location.reload(), 2000);
  };

  return (
    <Dialog open={modalOpen} onOpenChange={isOpen => setModalOpen(isOpen)}>
      <DialogTrigger variant="ghost">
        <div className="flex gap-1 font-medium">
          <Icons.SettingsFilled />
          Settings
        </div>
      </DialogTrigger>
      <DialogContent className="max-h-[98%] max-w-5xl">
        <DialogTitle className="flex items-top gap-4 mb-[10px] text-primary">
          <Icons.SettingsFilled />
          <div>
            <p className="text-xl leading-none">Settings</p>
          </div>
        </DialogTitle>

        <div className="flex gap-4 justify-between">
          <EmailSender />
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" className="min-h-[37px]" onClick={handleResetDivertFeature}>
              Reset Divert Feature
            </Button>
            <Button size="sm" variant="destructive" className="min-h-[37px]" onClick={handleResetSession}>
              Reset Session
            </Button>
          </div>
        </div>

        <div id="modal-content" className="modal-content">
          <div>
            <ApplicationContainer className="text-white w-max mb-4">
              <Icons.InfoTracLogo width={20} height={20} />
              InfoTRAC
            </ApplicationContainer>

            <div>
              <form
                className="menu-items grid grid-cols-3 gap-4"
                onSubmit={e => {
                  e.preventDefault();
                  handleSubmit(handleSaveInfoTrac);
                }}
              >
                {settingItems.map(item => (
                  <Controller
                    control={control}
                    key={item.path}
                    name={item.path}
                    defaultValue // default - true
                    render={({ field: { onChange, value, name } }) => (
                      <div className="flex items-center space-x-2" key={item.path}>
                        <Switch id={name} checked={!!value} onCheckedChange={onChange} {...register(name)} />
                        <Label htmlFor={item.path}>{item.label}</Label>
                      </div>
                    )}
                  />
                ))}
              </form>
            </div>
          </div>
          <div className="mt-6 ">
            <div className="flex gap-4 items-center">
              <ApplicationContainer className="text-white w-max mb-4">
                <Icons.GuidanceLogo />
                eStatements
              </ApplicationContainer>
            </div>
            <div className="mb-2">
              <div className="flex gap-4">
                <div className="flex" style={{ lineHeight: '40px' }}>
                  Logo image:
                </div>
                <div className="flex gap-4">
                  <Button
                    size="lg"
                    className="pl-4 pr-4"
                    onClick={e => {
                      const target = e.target as HTMLElement;
                      const nextSibling = target.nextElementSibling;

                      if (nextSibling) {
                        nextSibling.querySelector('input')?.click();
                      }
                    }}
                  >
                    <Icons.File className="mr-2" />
                    Select file
                  </Button>
                  <Input
                    type="file"
                    multiple={false}
                    accept="image/png, image/gif, image/jpeg, image/jpg"
                    style={{ display: 'none' }}
                    onChange={e => {
                      if (e.target?.files && e.target.files[0]) {
                        toBase64(e.target.files[0]).then(data => {
                          // setLogoImage(data as string);
                          dispatch({ logo: data as string });
                        });
                      }
                    }}
                  />
                </div>
              </div>

              {guidanceState.logo ? (
                <img src={guidanceState.logo} title="logo" alt="logo" className="mt-3 max-w-80 max-h-40" />
              ) : null}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 mt-4">
              <div>
                <Label className={labelStyles}>Delivery Type: (Monthly Statement)</Label>

                <RadioGroup
                  value={guidanceState.deliveryTypeMonthly ?? 'electronic'}
                  onValueChange={val => dispatch({ deliveryTypeMonthly: val as 'electronic' | 'paper' | 'both' })}
                  className="flex gap-4 flex-col sm:flex-row"
                >
                  {[
                    { value: 'electronic', label: 'Electronic' },
                    { value: 'paper', label: 'Paper' },
                    { value: 'both', label: 'Both' },
                  ].map(({ value, label }) => (
                    <div key={value} className="flex items-center space-x-2 child:m-0">
                      <RadioGroupItem value={value} id={`option-${value}`} />
                      <Label htmlFor={`option-${value}`} className={cn('text-sm')}>
                        {label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label className={labelStyles}>Delivery Type: (Visa)</Label>

                <RadioGroup
                  defaultValue="electronic"
                  className="flex gap-4 flex-col sm:flex-row"
                  value={guidanceState.deliveryTypeVisa ?? 'electronic'}
                  onValueChange={val => dispatch({ deliveryTypeVisa: val as 'electronic' | 'paper' | 'both' })}
                >
                  {[
                    { value: 'electronic', label: 'Electronic' },
                    { value: 'paper', label: 'Paper' },
                    { value: 'both', label: 'Both' },
                  ].map(({ value, label }) => (
                    <div key={value} className="flex items-center space-x-2 child:m-0">
                      <RadioGroupItem value={value} id={`option-${value}-visa`} />
                      <Label htmlFor={`option-${value}-visa`} className={cn('text-sm')}>
                        {label}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            </div>
            <div className="flex gap-8 items-center">
              <div className="flex items-center space-x-2">
                <Switch
                  id="showCaptcha"
                  defaultChecked
                  checked={guidanceState.showCaptcha}
                  onCheckedChange={checked => dispatch({ showCaptcha: checked })}
                />
                <Label htmlFor="showCaptcha">Show CAPTCHA</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="showPdfViewer"
                  defaultChecked
                  checked={guidanceState.showPdfViewer}
                  onCheckedChange={checked => dispatch({ showPdfViewer: checked })}
                />
                <Label htmlFor="showPdfViewer">Show Pdf Viewer</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Select
                  value={guidanceState.enrollmentStep3}
                  onValueChange={value => dispatch({ enrollmentStep3: value as EnrollmentStepType })}
                >
                  <SelectTrigger prefix="Enrollment step 3" className="w-full min-w-36" variant="default">
                    <SelectValue placeholder="Global" />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      { value: 'by-account', label: 'By Account' },
                      { value: 'global', label: 'Global' },
                    ].map(({ value, label }) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" size="sm" onClick={handleReset}>
                <Icons.SolarHistoryBold />
                Reset default
              </Button>
            </div>

            <div
              id="color-picker-container"
              className="guidance grid grid-cols-1 md:grid-cols-[320px,1fr] gap-4 mt-6 overflow-hidden"
            >
              <div>
                <div id="picker-selector" className="flex md:justify-between items-center gap-2 mb-4">
                  {colorPickers.map(({ colorType, label }) => (
                    <div
                      key={colorType}
                      role="button"
                      tabIndex={-1}
                      className={cn('rounded-lg border flex flex-col w-[140px] text-center p-0.5 relative', {
                        'border-addition6 before:text-addition6 border-2': activeColorPicker === colorType,
                        // eslint-disable-next-line max-len
                        'before:content-["▼"] before:absolute before:top-[86px] before:right-0 before:left-0 before:m-auto before:text-[10px]':
                          activeColorPicker === colorType,
                        // ▼ | ▾
                      })}
                      onClick={() => setActiveColorPicker(colorType)}
                    >
                      <p>{label} Color</p>
                      <div
                        className="h-[60px] w-full rounded-b-lg"
                        style={{
                          background: guidanceState[`${colorType}Color`] || `var(--${colorType})`,
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  {activeColorPicker === 'primary' ? (
                    // RgbaStringColorPicker
                    <HexColorPicker
                      color={guidanceState.primaryColor || defaultGuidanceValues.primaryColor}
                      onChange={val => dispatch({ primaryColor: val })}
                      className="!w-[320px]"
                    />
                  ) : (
                    <HexColorPicker
                      color={guidanceState.secondaryColor || defaultGuidanceValues.secondaryColor}
                      onChange={val => dispatch({ secondaryColor: val })}
                      className="!w-[320px]"
                    />
                  )}
                </div>
              </div>

              {/* className="rounded-lg border-2 border-slate-200 p-0.5" */}
              <div className="ml-32 lg:ml-0">
                <div
                  className={cn('preview-site-container w-max h-[320px] origin-top-left scale-[0.54] max-w-[400px]')}
                >
                  <PreviewDashboard
                    primaryColor={guidanceState.primaryColor || ''}
                    secondaryColor={guidanceState.secondaryColor || ''}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="!mt-4" />
        <div className="flex justify-end gap-4 z-10">
          <Button variant="link" className="py-3 hover:no-underline" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            className="py-3"
            onClick={() => {
              setModalOpen(false);
              handleSaveInfoTrac(getValues());
              handleSaveGuidance();

              handleTriggerRegenerate();

              notifySuccess('Saved');
            }}
          >
            <Icons.SaveDiskette />
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SettingsDialog;
